import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, X } from "lucide-react";
import { PhotoGallery } from "@/components/CompletionPhotos";
import { useEffect } from "react";

// Host reviews the cleaner's checklist + photos before the job is marked complete.
export default function TurnoverReviewModal({ booking, onClose, onApproved }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/bookings/${booking.booking_id}/photos`)
      .then((r) => setPhotos(r.data.photos))
      .finally(() => setLoading(false));
  }, [booking.booking_id]);

  const approve = async () => {
    setBusy(true);
    try {
      await api.post("/bookings/approve", { booking_id: booking.booking_id });
      toast.success("Turnover approved — job complete");
      onApproved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const checklist = booking.checklist || [];

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center sm:px-5 overflow-y-auto" onClick={onClose}>
      <div className="bg-card rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="turnover-review-modal">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-semibold">Review turnover</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{booking.property_summary?.name || booking.service_name} · {booking.date}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-secondary" data-testid="close-review" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-5">
          <h4 className="text-sm font-semibold">Cleaner's photos</h4>
          <div className="mt-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin text-accent" /> : <PhotoGallery bookingId={booking.booking_id} photos={photos} />}
          </div>
        </div>

        {checklist.length > 0 && (
          <div className="mt-5">
            <h4 className="text-sm font-semibold">Checklist</h4>
            <ul className="mt-2 space-y-1.5" data-testid="review-checklist">
              {checklist.map((i) => (
                <li key={i.id} className="flex items-center gap-2 text-sm">
                  <span className={`h-4 w-4 rounded flex items-center justify-center ${i.done ? "bg-accent text-white" : "border border-border"}`}>{i.done && <Check className="h-3 w-3" />}</span>
                  {i.label}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mt-6">
          <button onClick={approve} disabled={busy} className="flex-1 py-3 rounded-full bg-accent text-white font-medium hover:bg-accent/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="approve-turnover">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve & complete
          </button>
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-border font-medium hover:bg-secondary transition-colors" data-testid="review-later">Review later</button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">Not happy with the clean? Message your cleaner before approving — you can also report an issue from the conversation.</p>
      </div>
    </div>
  );
}
